import * as t from "io-ts";

import { NOT_SURE_VARIANT_CHOICE_SLUG } from "../apps/reviews/constants";
import {
    IProductUUID,
    IReviewsBrandID,
    ProductUUID,
    ReviewsBrandID,
    ReviewsProductID,
    ReviewsProductTypeID,
    ReviewsProductVariantID,
} from "./nominals";
import { nullable } from "./utils";

export enum ReviewSortOption {
    HIGHEST_RATED = "-rating,-created_datetime",
    LOWEST_RATED = "rating,-created_datetime",
    NEWEST = "-created_datetime",
    OLDEST = "created_datetime",
}

type IReviewParams = {
    brand_id: IReviewsBrandID;
    ordering?: ReviewSortOption;
    product_type_id?: string;
    page?: number;
    page_size?: number;
};

export type IReviewQueryFacets =
    | "text"
    | "rating"
    | "product_uuid"
    | "product_id"
    | "source_id"
    | "product_variant_id"
    | "product_type_id";

export type IReviewQuery = {
    [T in IReviewQueryFacets]?: string | number;
};

export type ISearchReviewArgs = IReviewParams & IReviewQuery;

const SearchFacetSubOption = t.readonly(
    t.type({
        name: t.union([t.string, t.number]),
        option_id: t.number,
        quantity: t.number,
    }),
);

export type ISearchFacetSubOption = t.TypeOf<typeof SearchFacetSubOption>;

const SearchFacetOption = t.readonly(
    t.intersection([
        SearchFacetSubOption,
        t.type({
            group_id: nullable(t.string),
            group_name: nullable(t.string),
            group_descr: nullable(t.string),
            group_sort_order: nullable(t.number),
            description: t.string,
            sub_option_type: nullable(
                t.keyof({
                    text: null,
                    rating: null,
                    product_id: null,
                    source_id: null,
                    product_variant_id: null,
                }),
            ),
            sub_options: t.readonlyArray(SearchFacetSubOption),
        }),
    ]),
);

export type ISearchFacetOption = t.TypeOf<typeof SearchFacetOption>;

const SearchFacet = t.readonly(
    t.type({
        id: t.number,
        type: t.keyof({
            text: null,
            rating: null,
            product_id: null,
            source_id: null,
            product_variant_id: null,
        }),
        is_conjunctive: t.boolean,
        options: t.readonlyArray(SearchFacetOption),
    }),
);

export type ISearchFacet = t.TypeOf<typeof SearchFacet>;

export const SearchFacets = t.readonlyArray(SearchFacet);

export const ReviewsBrand = t.readonly(
    t.type({
        id: ReviewsBrandID,
        name: t.string,
        facets: t.readonlyArray(t.number),
        num_reviews: t.number,
        average_rating: nullable(t.number),
    }),
);

export type IReviewsBrand = t.TypeOf<typeof ReviewsBrand>;

const Review = t.readonly(
    t.intersection([
        t.type({
            id: t.number,

            product_id: ReviewsProductID,
            product_name: t.string,
            product_short_name: t.string,

            product_category_name: nullable(t.string),
            product_category_slug: nullable(t.string),
            product_category_sort_order: nullable(t.number),

            brand_id: ReviewsBrandID,
            brand_name: t.string,

            product_type_id: nullable(ReviewsProductTypeID),
            product_type_name: nullable(t.string),
            product_type_sort_order: nullable(t.number),
            product_variant_id: nullable(ReviewsProductVariantID),

            name: t.string,
            location: t.string,
            rating: t.number,
            headline: t.string,
            text: t.string,
            sleep_style: nullable(t.string),
            is_recommended: nullable(t.boolean),
            source_id: t.number,
            source_name: t.string,
            created_datetime: t.string,
            modified_datetime: t.string,
            disclosure_code: t.string,
        }),
        t.partial({
            product_uuid: nullable(ProductUUID), // Product UUID field doesn't exist if no core product is mapped
            product_variant_slug: nullable(t.string),
            product_variant_display_name: nullable(t.string),

            highlighted: t.string,

            is_staff_reviewer: t.boolean,
            is_verified_buyer: t.boolean,
            is_verified_reviewer: t.boolean,
        }),
    ]),
);

export type IReview = t.TypeOf<typeof Review>;

export const ReviewResultsPage = t.readonly(
    t.type({
        count: t.number,
        next: nullable(t.string),
        previous: nullable(t.string),
        results: t.readonlyArray(Review),
    }),
);

export type IReviewResultsPage = t.TypeOf<typeof ReviewResultsPage>;

const WriteReviewTemplateFieldResponseType = t.keyof({
    text: null,
    multiple: null,
    single: null,
    numeric: null,
    email: null,
});

const WriteReviewTemplateFieldInputType = t.keyof({
    TextBox: null,
    TextArea: null,
    Stars: null,
    CheckboxVertical: null,
    RadioVertical: null,
    RadioHorizontal: null,
    RadioHorizontalLinked: null,
    Dropdown: null,
    AgeDisclosure: null,
});

const WriteReviewTemplateFieldType = t.keyof({
    simple: null,
    collection: null,
    composite: null,
});

const WriteReviewTemplateField = t.readonly(
    t.type({
        type: WriteReviewTemplateFieldResponseType,
        response_type: WriteReviewTemplateFieldResponseType,
        input_type: WriteReviewTemplateFieldInputType,
        field_id: nullable(t.string),
        field_type: WriteReviewTemplateFieldType,
        key: t.string,
        name: t.string,
        is_required: t.boolean,
        max_length: nullable(t.number),
        options: t.readonlyArray(t.string),
        is_tag: t.boolean,
        is_merchant_specific: t.boolean,
    }),
);

export type IWriteReviewTemplateField = t.TypeOf<
    typeof WriteReviewTemplateField
>;

export const WriteReviewTemplate = t.record(t.string, WriteReviewTemplateField);

export type IWriteReviewTemplate = t.TypeOf<typeof WriteReviewTemplate>;

const HistogramEntry = t.readonly(
    t.type({
        rating: t.number,
        count: t.number,
    }),
);

export type IHistogramEntry = t.TypeOf<typeof HistogramEntry>;

type IWriteReviewBlackBox = Readonly<{
    io: string;
    igloo: string;
}>;

export type IWriteReviewData = Readonly<{
    id: string;
    merchant_user_email?: string;
    product_id?: string;
    variant?: string;
    blackbox: Readonly<{
        io: string;
        igloo: string;
    }>;
    started: string;
    created: string;
    [fieldName: string]: string | IWriteReviewBlackBox | undefined;
}>;

const ReviewsProductVariant = t.readonly(
    t.type({
        id: ReviewsProductVariantID,
        slug: t.string,
        display_name: t.string,
        alt_product_id: t.string,
    }),
);

export type IReviewsProductVariant = t.TypeOf<typeof ReviewsProductVariant>;

export type IReviewsProductVariantChoices =
    | IReviewsProductVariant
    | null
    | typeof NOT_SURE_VARIANT_CHOICE_SLUG;

export const ReviewsProduct = t.readonly(
    t.intersection([
        t.type({
            id: ReviewsProductID,
            uuid: nullable(ProductUUID),
            brand: ReviewsBrandID,
            product_type: nullable(ReviewsProductTypeID),
            product_type_name: nullable(t.string),
            product_type_sort_order: t.number,
            category: nullable(t.number),
            name: t.string,
            short_name: nullable(t.string),
            product_id: t.string,
            num_reviews: t.number,
            average_rating: nullable(t.number),
            histogram: t.readonlyArray(HistogramEntry),
            variants: t.readonlyArray(ReviewsProductVariant),
        }),
        t.partial({
            component_of_uuids: t.readonlyArray(ProductUUID),
        }),
    ]),
);

export type IReviewsProduct = t.TypeOf<typeof ReviewsProduct>;

export const ReviewsProducts = t.readonlyArray(ReviewsProduct);

const ReviewsInfoMap = t.readonly(
    t.type({
        reviewsProductID: ReviewsProductID,
        reviewsProductVariantIDs: t.readonlyArray(ReviewsProductVariantID),
    }),
);

export const UUIDReviewsInfoMap = t.record(ProductUUID, ReviewsInfoMap);

export type IUUIDReviewsInfoMap = Map<
    IProductUUID,
    t.TypeOf<typeof ReviewsInfoMap>
>;
